import React, { useState, useMemo, useRef, useEffect, useContext, useCallback } from 'react';
// cspell:ignore refinedev
import { useList, HttpError, useCreate } from '@refinedev/core';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  UsersIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { semanticColors } from '../../theme';
import { PositionLegendItem, CreateNewShift } from '../../shared/types/shift';
import {
  formatShiftTime,
  parseISO,
  format,
  addDays,
  subDays,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
  isSameDay,
} from '../../shared/utils/dateUtils';
import { getPositionStyles } from '../../shared/utils/uiUtils';
import { getDisplayStatus } from '../../shared/utils/get-status-color';
import Loading from '../../components/loading/loading';
import PostShiftModal from './shift-drawer';
import { FacilityContext } from '../../components/layout';
import useApiService from '../../hooks/useApiService';

// Interface for the new API response structure
interface ApiOpeningData {
  opening_id: string;
  shift_id: string;
  start_time: string;
  end_time: string;
  status: string;
  position_id: string;
  position_name: string;
  applications_count: number;
  clinician_id: string | null;
  clinician_name: string | null;
}

interface ApiResponse {
  success: boolean;
  data: ApiOpeningData[];
  count: number;
}

// Interface for grouped shift data
interface GroupedShift {
  shift_id: string;
  start_time: string;
  end_time: string;
  position_name: string;
  openings: ApiOpeningData[];
  total_applications: number;
  facility_name?: string;
  city?: string;
}

const DayView: React.FC = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  const { facility } = useContext(FacilityContext);
  const api = useApiService();

  const initialDate = useMemo(() => {
    if (date) {
      try {
        const parsedDate = parseISO(date);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      } catch (e) {
        // Invalid date in URL
      }
    }
    return new Date(2025, 5, 5);
  }, [date]);

  const [currentDate, setCurrentDate] = useState<Date>(initialDate);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const now = new Date();
    return !isNaN(now.getTime()) ? now : initialDate;
  });
  const calendarRef = useRef<HTMLDivElement>(null);

  // New state for API data
  const [openingsData, setOpeningsData] = useState<ApiOpeningData[]>([]);
  const [isLoadingOpenings, setIsLoadingOpenings] = useState<boolean>(false);
  const [openingsError, setOpeningsError] = useState<string | null>(null);

  useEffect(() => {
    setCurrentDate(initialDate);
    setCalendarMonth(initialDate);
  }, [initialDate]);

  const { data: positionsResponse, isLoading: positionsLoading, error: positionsError } =
    useList<PositionLegendItem, HttpError>({
      resource: 'positions',
    });

  const { mutate: createShift, isLoading: isCreating } = useCreate<CreateNewShift>();

  // Function to fetch openings data
  const fetchOpeningsData = useCallback(async () => {
    if (!facility?.facility_id) {
      setOpeningsError('Facility ID not available');
      return;
    }

    try {
      setIsLoadingOpenings(true);
      setOpeningsError(null);

      // Format the date for the API call
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      // Get timezone dynamically
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Make API call to fetch shifts for the day
      const response = await api.get<ApiResponse>(
        `/shifts?view=day&date=${dateStr}&facilityId=${facility.facility_id}&timezone=${encodeURIComponent(timezone)}`
      );

      setOpeningsData(response.data.data);
    } catch (error) {
      console.error('Error fetching openings data:', error);
      setOpeningsError('Failed to load shift data');
    } finally {
      setIsLoadingOpenings(false);
    }
  }, [api, facility?.facility_id, currentDate]);

  // Fetch data when component mounts or date changes
  useEffect(() => {
    if (facility?.facility_id) {
      fetchOpeningsData();
    }
  }, [fetchOpeningsData, facility?.facility_id]);

  const handlePostShift = (shiftData: Partial<CreateNewShift>): void => {
    createShift(
      {
        resource: 'shifts',
        values: shiftData,
      },
      {
        onSuccess: () => {
          fetchOpeningsData(); // Refresh the data
          setIsModalOpen(false);
        },
        onError: () => {
          // Failed to create shift
        },
      }
    );
  };

  const positionsForLegend = positionsResponse?.data ?? [];

  // Group openings by shift_id to create shift-like objects
  const groupedShifts = useMemo(() => {
    const grouped: { [key: string]: GroupedShift } = {};

    openingsData.forEach((opening) => {
      if (!grouped[opening.shift_id]) {
        grouped[opening.shift_id] = {
          shift_id: opening.shift_id,
          start_time: opening.start_time,
          end_time: opening.end_time,
          position_name: opening.position_name,
          openings: [],
          total_applications: 0,
          facility_name: facility?.facility_name,
          city: facility?.city,
        };
      }
      
      grouped[opening.shift_id].openings.push(opening);
      grouped[opening.shift_id].total_applications += opening.applications_count;
    });

    return Object.values(grouped);
  }, [openingsData, facility]);

  const dayStats = useMemo(() => {
    const totalShifts = groupedShifts.length;
    
    // Calculate open positions only for non-cancelled shifts
    const openPositions = groupedShifts.reduce((acc, shift) => {
      // Get the primary status for the shift
      const getShiftStatus = () => {
        if (!shift.openings || shift.openings.length === 0) return 'UNKNOWN';
        
        const allCancelled = shift.openings.every(opening => opening.status === 'CANCELLED');
        if (allCancelled) return 'CANCELLED';
        
        // Find first non-cancelled opening status to display
        const firstNonCancelled = shift.openings.find(opening => opening.status !== 'CANCELLED');
        return firstNonCancelled?.status || 'UNKNOWN';
      };
      
      const shiftStatus = getShiftStatus();
      
      // Only count as "open" if shift is not cancelled
      if (shiftStatus !== 'CANCELLED') {
        return acc + (shift.openings?.filter((op) => op.status === 'ACTIVE').length ?? 0);
      }
      return acc;
    }, 0);
    
    // Calculate total applications only for active shifts
    const totalApplications = groupedShifts.reduce((acc, shift) => {
      const getShiftStatus = () => {
        if (!shift.openings || shift.openings.length === 0) return 'UNKNOWN';
        
        const allCancelled = shift.openings.every(opening => opening.status === 'CANCELLED');
        if (allCancelled) return 'CANCELLED';
        
        const firstNonCancelled = shift.openings.find(opening => opening.status !== 'CANCELLED');
        return firstNonCancelled?.status || 'UNKNOWN';
      };
      
      const shiftStatus = getShiftStatus();
      
      // Only count applications for non-cancelled shifts
      if (shiftStatus !== 'CANCELLED') {
        return acc + shift.total_applications;
      }
      return acc;
    }, 0);
    
    const facilities = new Set(groupedShifts.map((shift) => shift.facility_name).filter(Boolean));

    return {
      totalShifts,
      openPositions,
      totalApplications,
      facilitiesCount: facilities.size,
      facilitiesList: Array.from(facilities),
    };
  }, [groupedShifts]);

  const shiftsByPosition = useMemo(() => {
    const grouped: { [key: string]: GroupedShift[] } = {};

    groupedShifts.forEach((shift) => {
      const positionName = shift.position_name ?? 'Unknown';
      if (!grouped[positionName]) {
        grouped[positionName] = [];
      }
      grouped[positionName].push(shift);
    });

    return grouped;
  }, [groupedShifts]);

  // Helper function to get shift status (same logic as other files)
  const getShiftStatus = (shift: GroupedShift) => {
    if (!shift.openings || shift.openings.length === 0) return 'UNKNOWN';
    
    const allCancelled = shift.openings.every(opening => opening.status === 'CANCELLED');
    if (allCancelled) return 'CANCELLED';
    
    // Find first non-cancelled opening status to display
    const firstNonCancelled = shift.openings.find(opening => opening.status !== 'CANCELLED');
    return firstNonCancelled?.status || 'UNKNOWN';
  };

  const handlePrevDay = (): void => {
    const newDate = subDays(currentDate, 1);
    setCurrentDate(newDate);
    navigate(`/shift-calendar/day/${format(newDate, 'yyyy-MM-dd')}`);
  };

  const handleNextDay = (): void => {
    const newDate = addDays(currentDate, 1);
    setCurrentDate(newDate);
    navigate(`/shift-calendar/day/${format(newDate, 'yyyy-MM-dd')}`);
  };

  const handleWeekView = (): void => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    navigate(`/shift-calendar/week/${format(weekStart, 'yyyy-MM-dd')}`);
  };

  const handleDayView = (day: Date): void => {
    const dateStr = format(day, 'yyyy-MM-dd');
    navigate(`/shift-calendar/day/${dateStr}`);
    setIsCalendarOpen(false);
  };

  const handlePrevMonth = (): void => {
    setCalendarMonth(subMonths(calendarMonth, 1));
  };

  const handleNextMonth = (): void => {
    setCalendarMonth(addMonths(calendarMonth, 1));
  };

  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const daysArray: Date[] = eachDayOfInterval({ start, end });
    const firstDayOfMonth = getDay(start);
    const paddingDaysStart: undefined[] = Array.from({ length: firstDayOfMonth }, () => undefined);
    const totalCells = 42;
    const filledCells = paddingDaysStart.length + daysArray.length;
    const paddingDaysEnd: undefined[] = Array.from(
      { length: totalCells - filledCells },
      () => undefined
    );
    return [...paddingDaysStart, ...daysArray, ...paddingDaysEnd];
  }, [calendarMonth]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const today = new Date();

  if (isLoadingOpenings || positionsLoading) return <Loading />;
  if (openingsError) return <p className="text-[#C84648]">Error loading shifts: {openingsError}</p>;
  if (positionsError) return <p className="text-[#C84648]">Error loading positions: {positionsError.message}</p>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: semanticColors.background.primary }}>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl" style={{ background: semanticColors.background.gradient }}>
        <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
        <div className="relative px-4 py-4 sm:px-6 sm:py-6 lg:px-6 lg:py-4">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  className="flex items-center rounded-lg bg-white/20 backdrop-blur-sm p-1.5 text-white transition hover:bg-white/30 sm:p-2 lg:p-1.5"
                  aria-label="Open calendar"
                >
                  <CalendarDaysIcon className="size-5 sm:size-6 lg:size-5" />
                </button>
                <div>
                                  <h1 
                  className="text-2xl font-bold sm:text-3xl lg:text-2xl"
                    style={{
                      background: 'linear-gradient(to right, rgb(245, 185, 87), rgb(255, 237, 205), rgb(171, 109, 10))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    Day View
                  </h1>
                  <p className="mt-1 text-sm text-white/90 sm:text-base lg:text-sm">
                    {format(currentDate, 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/30 sm:px-4 sm:py-2 sm:text-sm lg:px-3 lg:py-1.5 lg:text-xs"
                >
                  Day View
                </button>
                <button
                  onClick={handleWeekView}
                  className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/30 sm:px-4 sm:py-2 sm:text-sm lg:px-3 lg:py-1.5 lg:text-xs"
                >
                  Week View
                </button>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/30 sm:px-4 sm:py-2 sm:text-sm lg:px-3 lg:py-1.5 lg:text-xs"
                >
                  Post Shift
                </button>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={handlePrevDay}
                    className="rounded-lg bg-white/20 backdrop-blur-sm p-1.5 text-white transition hover:bg-white/30 sm:p-2 lg:p-1.5"
                    aria-label="Previous day"
                  >
                    <ChevronLeftIcon className="size-4 sm:size-5 lg:size-4" />
                  </button>
                  <button
                    onClick={handleNextDay}
                    className="rounded-lg bg-white/20 backdrop-blur-sm p-1.5 text-white transition hover:bg-white/30 sm:p-2 lg:p-1.5"
                    aria-label="Next day"
                  >
                    <ChevronRightIcon className="size-4 sm:size-5 lg:size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="px-3 py-3 sm:px-4 sm:py-4 lg:px-3 lg:py-3">
        {isCalendarOpen && (
          <div
            ref={calendarRef}
            className="fixed left-8 top-20 z-50 w-[320px] rounded-xl border border-gray-200 bg-white p-4 shadow-2xl sm:left-12 sm:w-[360px] sm:p-5"
          >
            <div className="mb-4 flex items-center justify-between">
              <button
                onClick={handlePrevMonth}
                className="rounded-full p-2 transition-colors hover:bg-gray-100 hover:shadow-sm"
                aria-label="Previous month"
              >
                <ChevronLeftIcon className="size-4 text-gray-600" />
              </button>
              <span className="text-base font-semibold text-gray-800">
                {format(currentDate, 'MMMM yyyy')}
              </span>
              <button
                onClick={handleNextMonth}
                className="rounded-full p-2 transition-colors hover:bg-gray-100 hover:shadow-sm"
                aria-label="Next month"
              >
                <ChevronRightIcon className="size-4 text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-2 text-center mb-3">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-sm font-semibold text-gray-600 py-1">
                  {day}
                </div>
              ))}
              {calendarDays.map((day, index) => {
                const isDate = day instanceof Date;
                const isToday = isDate && isSameDay(day, today);
                const isSelected = isDate && isSameDay(day, currentDate);
                return (
                  <button
                    key={index}
                    onClick={() => isDate && handleDayView(day)}
                    className={`size-8 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-sm ${
                      isDate
                        ? isSelected
                          ? 'bg-blue-600 text-white shadow-md'
                          : isToday
                          ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        : 'invisible'
                    }`}
                    disabled={!isDate}
                    aria-label={isDate ? format(day, 'MMMM d, yyyy') : undefined}
                  >
                    {isDate ? format(day, 'd') : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      <div className="mb-3 flex flex-col flex-wrap gap-1 sm:mb-4 sm:flex-row sm:gap-2 lg:mb-3">
        <span className="text-xs font-medium text-[#677D94]">Position Legend:</span>
        {positionsForLegend.map((pos) => {
          const style = getPositionStyles(pos.position_name);
          return (
            <span
              key={pos.position_id}
              className={`rounded-full px-2 py-0.5 text-[0.625rem] font-bold sm:px-2 sm:py-1 sm:text-xs ${style.badge}`}
            >
              {pos.position_name.toUpperCase()}
            </span>
          );
        })}
      </div>

      <div className="mb-3 sm:mb-4 lg:mb-3">
        <h2 className="mb-2 text-sm font-semibold text-[#2A333C] sm:text-base lg:text-sm">Day Overview</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4 lg:gap-2">
          <div className="rounded-lg border border-[#E0E8ED] bg-amber-50 p-2 sm:p-3 lg:p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.625rem] font-medium text-[#AB6D0A] sm:text-sm lg:text-[0.625rem]">Total Shifts</p>
                <p className="text-sm font-bold text-[#F5B957] sm:text-base lg:text-sm">{dayStats.totalShifts}</p>
              </div>
              <div className="flex size-6 items-center justify-center rounded-full bg-amber-100 sm:size-8 lg:size-6">
                <ClockIcon className="size-3 text-[#AB6D0A] sm:size-4 lg:size-3" />
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-[#E0EDE2] bg-green-50 p-2 sm:p-3 lg:p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.625rem] font-medium text-[#3A7D44] sm:text-sm lg:text-[0.625rem]">Open Positions</p>
                <p className="text-sm font-bold text-[#2E6B3A] sm:text-base lg:text-sm">{dayStats.openPositions}</p>
              </div>
              <div className="flex size-6 items-center justify-center rounded-full bg-white sm:size-8 lg:size-6">
                <UsersIcon className="size-3 text-[#3A7D44] sm:size-4 lg:size-3" />
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-[#FFF8E6] bg-amber-50 p-2 sm:p-3 lg:p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.625rem] font-medium text-[#D4A123] sm:text-sm lg:text-[0.625rem]">Applications</p>
                <p className="text-sm font-bold text-[#AB6D0A] sm:text-base lg:text-sm">{dayStats.totalApplications}</p>
              </div>
              <div className="flex size-6 items-center justify-center rounded-full bg-amber-100 sm:size-8 lg:size-6">
                <span className="text-xs text-[#D4A123] sm:text-sm lg:text-xs">📋</span>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-[#FCE8D9] bg-orange-50 p-2 sm:p-3 lg:p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[0.625rem] font-medium text-[#E67E34] sm:text-sm lg:text-[0.625rem]">Facilities</p>
                <p className="text-sm font-bold text-[#BF5A1F] sm:text-base lg:text-sm">{dayStats.facilitiesCount}</p>
              </div>
              <div className="flex size-6 items-center justify-center rounded-full bg-orange-100 sm:size-8 lg:size-6">
                <MapPinIcon className="size-3 text-[#E67E34] sm:size-4 lg:size-3" />
              </div>
            </div>
          </div>

        </div>
        <div className="mt-2 flex flex-col text-[0.625rem] text-[#677D94] sm:flex-row sm:items-center sm:justify-between lg:text-[0.625rem]">
          <span>{dayStats.facilitiesCount} Facilities: {dayStats.facilitiesList.join(', ')}</span>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-3 lg:space-y-2">
        {Object.entries(shiftsByPosition).map(([positionName, shifts]) => {
          const positionStats = {
            shiftsCount: shifts.length,
            openingsCount: shifts.reduce((acc, shift) => {
              const shiftStatus = getShiftStatus(shift);
              // Only count active openings for non-cancelled shifts
              if (shiftStatus !== 'CANCELLED') {
                return acc + (shift.openings?.filter((op) => op.status === 'ACTIVE').length ?? 0);
              }
              return acc;
            }, 0),
            facilitiesCount: new Set(shifts.map((shift) => shift.facility_name)).size,
            applicationsCount: shifts.reduce((acc, shift) => {
              const shiftStatus = getShiftStatus(shift);
              // Only count applications for non-cancelled shifts
              if (shiftStatus !== 'CANCELLED') {
                return acc + shift.total_applications;
              }
              return acc;
            }, 0),
          };
          const positionStyle = getPositionStyles(positionName);
          return (
            <div key={positionName} className="rounded-lg border border-[#CBD5E1] bg-white shadow-sm">
              <div className={`border-b border-[#CBD5E1] px-2 py-1 sm:px-3 sm:py-2 lg:px-2 lg:py-1 ${positionStyle.lightBg}`}>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <span className={`rounded-full px-2 py-0.5 text-[0.625rem] font-bold sm:px-2 sm:py-1 sm:text-xs ${positionStyle.badge}`}>
                      {positionName.toUpperCase()}
                    </span>
                    <div className="flex flex-wrap items-center gap-1 text-[0.625rem] text-[#677D94] sm:gap-2 sm:text-sm">
                      <div className="flex items-center">
                        <ClockIcon className="mr-0.5 size-2 sm:size-4" />
                        <span>{positionStats.shiftsCount} shifts</span>
                      </div>
                      <div className="flex items-center">
                        <UsersIcon className="mr-0.5 size-2 sm:size-4" />
                        <span>{positionStats.openingsCount} openings</span>
                      </div>
                      <div className="flex items-center">
                        <MapPinIcon className="mr-0.5 size-2 sm:size-4" />
                        <span>{positionStats.facilitiesCount} facilities</span>
                      </div>
                    </div>
                  </div>

                  {/* Show status summary for the position */}
                  <div className="flex items-center text-[0.625rem] text-[#677D94] sm:text-sm">
                    <span>
                      {(() => {
                        const statusCounts = {
                          ACTIVE: shifts.filter(s => getShiftStatus(s) === 'ACTIVE').length,
                          CONFIRMED: shifts.filter(s => getShiftStatus(s) === 'CONFIRMED').length,
                          CANCELLED: shifts.filter(s => getShiftStatus(s) === 'CANCELLED').length,
                          COMPLETED: shifts.filter(s => getShiftStatus(s) === 'COMPLETED').length,
                          SELECTED: shifts.filter(s => getShiftStatus(s) === 'SELECTED').length,
                        };
                        
                        const activeCount = statusCounts.ACTIVE;
                        const confirmedCount = statusCounts.CONFIRMED;
                        const cancelledCount = statusCounts.CANCELLED;
                        
                        if (cancelledCount > 0) {
                          return `${activeCount} ${getDisplayStatus('ACTIVE').toLowerCase()}, ${confirmedCount} ${getDisplayStatus('CONFIRMED').toLowerCase()}, ${cancelledCount} ${getDisplayStatus('CANCELLED').toLowerCase()}`;
                        } else if (confirmedCount > 0) {
                          return `${activeCount} ${getDisplayStatus('ACTIVE').toLowerCase()}, ${confirmedCount} ${getDisplayStatus('CONFIRMED').toLowerCase()}`;
                        } else {
                          return `${activeCount} ${getDisplayStatus('ACTIVE').toLowerCase()}`;
                        }
                      })()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-2 p-2 sm:space-y-2 sm:p-3 lg:p-2">
                {shifts.map((shift) => {
                  const shiftStatus = getShiftStatus(shift);
                  const isShiftCancelled = shiftStatus === 'CANCELLED';
                  
                  // Only show active openings for non-cancelled shifts
                  const activeOpeningsCount = isShiftCancelled ? 0 : (shift.openings?.filter((op) => op.status === 'ACTIVE').length ?? 0);
                  
                  // Only show applications for non-cancelled shifts
                  // const appliedCount = isShiftCancelled ? 0 : shift.total_applications;
                  
                  // Get status styling
                  const getStatusStyle = (status: string) => {
                    switch (status.toUpperCase()) {
                      case 'CONFIRMED':
                        return 'bg-green-100 text-green-700 border-green-200';
                      case 'ACTIVE':
                        return 'bg-blue-100 text-blue-700 border-blue-200';
                      case 'CANCELLED':
                        return 'bg-red-100 text-red-700 border-red-200';
                      case 'SELECTED':
                        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
                      case 'COMPLETED':
                        return 'bg-green-100 text-green-700 border-green-200';
                      case 'EXPIRED':
                        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
                      case 'NO_SHOW':
                        return 'bg-red-100 text-red-700 border-red-200';
                      case 'MISSED_CLOCK_IN':
                      case 'MISSED_CLOCK_OUT':
                        return 'bg-orange-100 text-orange-700 border-orange-200';
                      default:
                        return 'bg-gray-100 text-gray-700 border-gray-200';
                    }
                  };
                  
                  // Get the first opening ID for the link (since we're showing shift details for a specific opening)
                  const firstOpeningId = shift.openings && shift.openings.length > 0 ? shift.openings[0].opening_id : null;
                  
                  return (
                    <Link
                      to={firstOpeningId ? `/shift-calendar/shifts/${shift.shift_id}/${firstOpeningId}` : '#'}
                      key={shift.shift_id}
                      className={`block rounded-lg p-2 transition-colors hover:bg-gray-100 sm:p-3 ${
                        isShiftCancelled ? 'bg-red-50' : 'bg-[#F8FAFC]'
                      } ${!firstOpeningId ? 'pointer-events-none opacity-50' : ''}`}
                      title={!firstOpeningId ? 'No openings available' : ''}
                    >
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                        <div className="flex-1">
                          <h3 className="text-xs font-medium text-[#2A333C] sm:text-base">
                            {shift.facility_name ?? 'N/A Facility'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-1 text-[0.625rem] text-[#677D94] sm:gap-2 sm:text-sm">
                            <div className="flex items-center">
                              <ClockIcon className="mr-0.5 size-2 sm:size-3" />
                              <span>{formatShiftTime(shift.start_time)}</span>
                            </div>
                            <div className="flex items-center">
                              <MapPinIcon className="mr-0.5 size-2 sm:size-3" />
                              <span>{shift.city ?? 'N/A'}</span>
                            </div>
                          </div>
                          
                          {/* Only show openings and applications for non-cancelled shifts */}
                          {!isShiftCancelled && (
                            <div className="mt-1 flex items-center gap-1 sm:mt-2 sm:gap-2">
                              <div className="flex items-center text-[0.625rem] text-[#3A7D44] sm:text-sm">
                                <UsersIcon className="mr-0.5 size-2 sm:size-3" />
                                <span className="font-medium">{activeOpeningsCount}</span>
                              </div>

                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="mt-1 flex items-center justify-end gap-1 sm:gap-2">
                            {/* Status badge */}
                            <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium border ${getStatusStyle(shiftStatus)}`}>
                              {getDisplayStatus(shiftStatus)}
                            </span>
                            

                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
        {Object.keys(shiftsByPosition).length === 0 && (
          <div className="rounded-lg border border-[#CBD5E1] bg-white py-6 text-center shadow-sm sm:py-8">
            <div className="py-2 sm:py-4">
              <ClockIcon className="mx-auto mb-1 size-3 text-[#94A3B8] sm:mb-2 sm:size-4" />
              <h3 className="text-sm font-medium text-[#2A333C] sm:text-lg">No shifts for this day</h3>
              <p className="text-[0.625rem] text-[#677D94] sm:text-sm">Check another date or create a new shift.</p>
            </div>
          </div>
        )}
      </div>

      </main>

      <PostShiftModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handlePostShift}
        isSubmitting={isCreating}
      />
    </div>
  );
};

export default DayView;
